import {
  COLS,
  DOOZ_HAND_COUNTS,
  dropFallMs,
  EMPTY,
  OPENING_ANNOUNCE_MS,
  ROWS,
  type DoozMove,
  type DoozView,
} from "@bing/engine-dooz";
import type { DoozMatchLength } from "@bing/shared";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Avatar } from "../components/Avatar";
import { GameIcon } from "../components/GameIcon";
import { displayName, type DisplayNameParts } from "../components/profile-ui";
import {
  DEFAULT_DOOZ_SEAT0_COLOR,
  DOOZ_PIECE_COLORS,
  doozSeatColor,
  doozTone,
  type DoozPieceColor,
} from "../game/dooz-colors";
import { Die3D } from "../game/backgammon-dice";
import { useDoozSfx } from "../game/dooz-sfx";
import { useBoxHeight } from "../game/use-fit-square";
import { TurnTimer, type TableTurnTimer } from "../game/online-lifecycle";
import { TURN_GLOW_SETTLE, TURN_GLOW_STEADY } from "../game/TableSeat";
import { fa } from "../i18n/fa";

/* ------------------------------------------------------------------------------------------ *
 * دوز — the press-and-release board.
 *
 * THE WHOLE COLUMN IS THE TAP TARGET (the brief), which is exactly why the preview exists: with
 * no per-cell control the player cannot otherwise see where their piece will land. Holding a
 * finger anywhere in a column shows a faint disc in the CELL IT WOULD LAND IN, sliding across as
 * the finger does, and releasing drops it there.
 *
 * ⚠️ THE LANDING CELL COMES FROM THE SERVER (`view.landing[col]`), never from counting the pieces
 * here. The preview and the drop that follows it must name the same cell, and the only way to
 * guarantee that is for both to come from one arithmetic.
 *
 * ⚠️ POINTER EVENTS, NOT CLICK. A tap IS pointerdown + pointerup, so mouse, touch and pen all work
 * through one path; there is deliberately no `onClick`, which would fire a SECOND time after every
 * mouse release. A probe therefore has to dispatch real `PointerEvent`s (or drive CDP's mouse,
 * which synthesises them) — see `apps/web/tools/dooz-probes/`.
 * ------------------------------------------------------------------------------------------ */

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"] as const;
function faNum(value: number | string): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/** Base <button> reset (Tailwind preflight is off → reset explicitly). */
const BTN = "appearance-none m-0 cursor-pointer";

/**
 * THE COLOUR **SEAT 0** PLAYS IN — not «mine».
 *
 * ⚠️ IT IS A SEAT FACT, SO BOTH SCREENS AGREE. Each side derives its own and its opponent's from
 * this one value (`doozSeatColor`), so the disc in a given cell is the same colour on both
 * devices. Solo and a private host set it from their own pick (they are seat 0); a public table
 * leaves it at the default, because there is nobody to ask for the stranger who joins.
 */
export const DoozColorContext = createContext<DoozPieceColor>(DEFAULT_DOOZ_SEAT0_COLOR);

/** Pick your own piece colour — offline and on a table you host. Two options, and the opponent
 *  takes the other one. */
export function DoozColorSelect({
  value,
  onChange,
}: {
  value: DoozPieceColor;
  onChange: (color: DoozPieceColor) => void;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={fa.dooz.color}
      data-testid="dooz-colors"
      className="flex w-full justify-center gap-3 rounded-xl border border-outline-variant bg-surface-container/70 p-2"
    >
      {DOOZ_PIECE_COLORS.map((color) => {
        const tone = doozTone(color);
        const active = color === value;
        return (
          <button
            key={color}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={fa.dooz.colorNames[color]}
            data-color={color}
            onClick={() => onChange(color)}
            className={`${BTN} rounded-full transition-transform active:scale-95`}
            style={{
              width: 34,
              height: 34,
              background: `radial-gradient(circle at 32% 30%, ${tone.light} 0%, ${tone.base} 68%, ${tone.deep} 100%)`,
              border: active ? "2px solid #fffdf0" : "1px solid rgba(0,0,0,0.45)",
              boxShadow: active
                ? `0 0 0 2px ${tone.base}, 0 0 10px ${tone.base}`
                : "0 2px 4px rgba(0,0,0,0.45)",
            }}
          />
        );
      })}
    </div>
  );
}

/** How many دست the match runs — ۳ or ۵. Solo start screen and the private host's settings. */
export function DoozHandsSelect({
  value,
  onChange,
  disabled = false,
}: {
  value: number;
  onChange: (hands: DoozMatchLength) => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={fa.dooz.hands}
      data-testid="dooz-hands"
      className="flex w-full gap-1 rounded-xl border border-outline-variant bg-surface-container/70 p-1"
    >
      {DOOZ_HAND_COUNTS.map((n: DoozMatchLength) => {
        const active = n === value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(n)}
            className={`${BTN} flex-1 rounded-lg px-2 py-2 text-body-md font-bold transition-colors ${
              active
                ? "bg-secondary text-ink-on-gold shadow-sm"
                : "bg-transparent text-on-surface-variant hover:text-on-surface"
            } ${disabled ? "opacity-50" : ""}`}
          >
            {n === 3 ? fa.dooz.hands3 : fa.dooz.hands5}
          </button>
        );
      })}
    </div>
  );
}

export interface DoozIdentity extends DisplayNameParts {
  avatarId?: string | null;
  avatarPhotoId?: string | null;
  isOfficial?: boolean;
}

/**
 * ONE SEAT'S CHIP — avatar, name, its piece swatch, and the seat's own turn ring.
 *
 * ⚠️ THE TURN CLOCK LIVES **HERE**, ON THE PLAYER IT BELONGS TO (operator, 2026-09-03), exactly as
 * تخته نرد draws it. It used to sit in the hint line under the board, where it named nobody: on a
 * two-seat table a countdown floating between the players is ambiguous about whose it is, which is
 * the one thing a turn clock must never be.
 *
 * ⚠️ AND THE NAME MUST NOT PUSH ANYTHING OUT OF THE ROW. It is the only variable-width thing on
 * the chip, so it is the one element allowed to shrink (`min-w-0` + `truncate` on a `flex-1`);
 * every sibling is `flex-none`. Without the `min-w-0` a long name sets the flex base and the timer
 * leaves the container — flexbox will not shrink a text node below its content width otherwise.
 */
function SeatStrip({
  name,
  who,
  color,
  active,
  mine,
  slot,
}: {
  name: string;
  who: DoozIdentity | null;
  color: DoozPieceColor;
  active: boolean;
  /** YOUR chip (card-12, the operator 2026-10-28): on your turn its ring plays the turn glow once
   *  and holds it (`TURN_GLOW_SETTLE`); the opponent's ring on its turn is the steady glow from the
   *  first instant, with no animation (`TURN_GLOW_STEADY`). */
  mine: boolean;
  /**
   * THE ROW'S ONE END SLOT — the turn clock during play, the «تاس بریز» button during the opening
   * contest, nothing otherwise.
   *
   * ⚠️ ONE SLOT ON PURPOSE, AND FIXED IN HEIGHT. The two never appear together (the contest runs
   * under a SHARED window, broadcast with seat −1, so no seat's clock is drawn — confirmed on the
   * running system, not assumed), but «they never collide today» is not a layout guarantee. One
   * slot means they cannot occupy the same space even in principle, and a fixed height means
   * neither can change the row's height when it appears or goes — which is what reflows the board.
   * The width is free: the NAME beside it is the flex item that truncates, so it absorbs the
   * difference without moving anything vertically.
   */
  slot?: ReactNode;
}): ReactNode {
  const tone = doozTone(color);
  return (
    <div className="flex w-full items-center gap-2" data-testid="dooz-seat">
      <div
        className="flex flex-none items-center justify-center rounded-full"
        style={{
          padding: 2,
          boxSizing: "border-box",
          boxShadow: active
            ? mine
              ? "0 0 14px 3px rgb(var(--c-secondary) / .4), 0 0 0 2px rgb(var(--c-secondary))"
              : TURN_GLOW_STEADY
            : "0 0 0 1px rgb(var(--c-outline-variant))",
          animation: active && mine ? TURN_GLOW_SETTLE : undefined,
        }}
      >
        <Avatar
          avatarId={who?.avatarId ?? ""}
          avatarPhotoId={who?.avatarPhotoId ?? null}
          size={34}
          official={who?.isOfficial === true}
        />
      </div>
      <span
        aria-hidden
        data-testid="dooz-seat-swatch"
        data-color={color}
        className="flex-none rounded-full"
        style={{
          width: 16,
          height: 16,
          background: `radial-gradient(circle at 32% 30%, ${tone.light} 0%, ${tone.base} 68%, ${tone.deep} 100%)`,
          border: "1px solid rgba(0,0,0,.45)",
        }}
      />
      {/*
       * THE NAME — the ONE element allowed to shrink, and the only variable-width thing here.
       *
       * ⚠️ THE VERIFIED BADGE'S DEFINED PLACE IS THE AVATAR DISC ABOVE, not this row. `Avatar`
       * stamps it absolutely INSIDE a box pinned to the avatar's own size, so an official account
       * contributes zero layout of its own and can never push the name or the clock by a pixel —
       * that is the platform's rule for every table, proven by construction rather than tuned.
       * A second tick beside the name would both duplicate it and reintroduce exactly the shove
       * this chip is being fixed for.
       */}
      <span
        data-testid="dooz-seat-name"
        className="m-0 min-w-0 flex-1 truncate font-body-lg text-body-lg text-on-surface"
      >
        {name}
      </span>
      {/*
       * ⚠️ ONE CLOCK, ON WHOEVER'S TURN IT IS (operator, 2026-09-03). دوز has two seats, so a
       * countdown drawn on BOTH chips is the same number twice — one of them always counting down
       * somebody else's turn. The container passes the clock to the seat on turn and to no other.
       */}
      <div
        className="flex flex-none items-center justify-end"
        data-testid="dooz-seat-slot"
        style={{ height: 38, minWidth: slot ? undefined : 0 }}
      >
        {slot}
      </div>
    </div>
  );
}

/** The disc that lives in a settled cell. */
function Disc({ color, size }: { color: DoozPieceColor; size: number }): ReactNode {
  const tone = doozTone(color);
  return (
    <span
      aria-hidden
      style={{
        display: "block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle at 34% 30%, ${tone.light} 0%, ${tone.base} 62%, ${tone.deep} 100%)`,
        boxShadow: `inset 0 -2px 4px rgba(0,0,0,.35), 0 1px 2px rgba(0,0,0,.4)`,
      }}
    />
  );
}

/**
 * The reserved height of each player's DIE ZONE — one above the opponent's chip, one below yours.
 *
 * ⚠️ RESERVED WHETHER OR NOT A DIE IS IN IT. The board is fitted to the height left over, so a row
 * that appears when the contest starts and vanishes when it ends re-fits the board and everything
 * on screen jumps — «after the roll the player profiles jump upward». Big enough for a die that is
 * actually worth looking at, since this is otherwise empty space.
 */
const DIE_ZONE_MIN = 52;
/**
 * …and it never grows past this.
 *
 * ⚠️ THE ZONE MAY BE ROOMY; THE DIE MAY NOT. Sizing the cube to the space available made it
 * enormous on a tall phone — «the die is too big». The ZONE still takes the spare room (that is
 * what keeps the board from moving when the dice come and go); the die inside it is a normal die.
 */
const DIE_ZONE_MAX = 116;
/** The cube's edge. A die, not a poster — the same order of size منچ and تخته نرد throw. */
const DIE_SIZE = 40;
/**
 * How far each die is nudged AWAY from the middle of the screen inside its OWN zone — the
 * opponent's down by this, yours up by the same — reported as «the opponent's die lands where it
 * overlaps «دست X از Y»».
 *
 * ⚠️ THAT OVERLAP WAS STRUCTURAL, 2 px, at every viewport where the die zone is at its
 * {@link DIE_ZONE_MIN} floor: the counter pill then hung under the title to `58 + safeTop`, and the
 * opponent cube's HIGHEST possible top is `50 + safeTop` (the column's own padding) `+ 7` (the
 * slot's padding round a 40 px cube) `- 1` (a 54 px slot centred in a 52 px zone overhangs by 1)
 * `= 56 + safeTop`. Measured then on the deployed staging build: 1024×700 +2.0, 320×568 +1.5,
 * 440×640 +0.5, 360×640 clear by only 1.0. Six, not two: two would erase the overlap and nothing
 * else; six left 4 px of daylight, spent against a clearance that can afford it — the cube's gap to
 * the seat chip on its other side is 16 px at the worst viewport, so this leaves 10.
 *
 * ⚠️ SINCE 2026-10-08 THE COUNTER IS IN THE SCORE BOX and nothing in the header reaches below the
 * buttons' band, `50 + safeTop` (`tools/dooz-probes/probe-header.cjs`, the contest's tie frame):
 * the nudged cube clears it by 12.0–15.0 px wherever the zone sits at its floor (320×568, 360×640,
 * 375×667, 440×640, the 307–440 px desktop panels), 28.5 at 384×823, 67 at 576×886. It stays six —
 * the dice are not moved for a collision that no longer exists. The one size it does not clear is
 * 256×393, the smallest desktop panel, whose felt column is taller than the screen: centring
 * overflows it upwards and the cube rides 3.5 px under the score box's bottom edge for the
 * contest's few seconds (it rode 11.5 px into the old counter pill there) — the board's fit, not
 * this nudge; named, not cleared.
 *
 * ⚠️ IT IS `position: relative`, SO IT COSTS THE LAYOUT NOTHING. The zone's height is reserved
 * whether or not a die is in it (the whole reason the board does not jump when the contest ends);
 * a relative offset moves what is PAINTED and leaves that reservation, the board's fit and every
 * other row exactly where they were. Not a `transform` — the die's own throw is a transform, and
 * this must not touch the animation.
 */
const DIE_NUDGE = 6;

/*
 * THE SCORE BOX NEVER CHANGES WIDTH — each column's CONTENT is a fixed px width just over the widest
 * value it can ever hold, the padding outside it (`box-content`). Vazirmatn's digits are NOT
 * tabular (`tabular-nums` is inert in it): ۳ is 10.86 px in the box's 16 px bold, ۱ 4.94, so a
 * column sized by its content changes width as the numbers move — the حکم defect the operator
 * already paid for (`HokmTable`'s SCORE_COL). MEASURED in the browser (`tools/dooz-probes/
 * probe-header.cjs`, 2026-10-08) over every value the rules allow:
 *   hand  «۱ از ۳» … «۵ از ۵» — the widest «۳ از ۳», 42.80 px (its label «دست» is 26.36)
 *   شما   the label (19.48 px) over 0…3 — a seat wins at most ⌈5/2⌉ = 3 hands (`matchDecided`)
 *   حریف  the label (27.56 px) over 0…3
 * Inline px, not classes: happy-dom reads them (`dooz-table.test.tsx` pins them).
 */
const SCORE_COL = { hand: 43, you: 20, them: 28 } as const;

/*
 * THE CHAT TOGGLE'S FOOTPRINT, kept free on every ONLINE table — حکم's rule (`HokmTable`'s
 * CHAT_TOGGLE_PX). `TableChat`'s closed toggle is `position: fixed`, outside this row's flow: 40 ×
 * 40, its top this row's own padding, and `insetInlineStart: calc(max(0px, (100vw - 440px) / 2) +
 * 64px)` — 16 (`px-4`) + 40 (the exit) + 8 (`gap-2`) in from the inline-start edge, exactly the
 * slot after the exit. Without the slot «دوز», centred in what is left, sat under the toggle.
 */
const CHAT_TOGGLE_PX = 40;

/** How much of a cell the disc fills. */
const DISC_RATIO = 0.82;
/** The board's own padding around the grid, in units of a cell. */
const FRAME_RATIO = 0.16;
/** The board never grows past this on a large screen. */
const MAX_CELL = 62;
/**
 * Height inside the board column already spoken for by the chrome around it, reserved from the
 * HEIGHT axis so the board is never fitted to space that is already taken and then clipped.
 *
 * The rows: two seat chips (38 each), the hint line (30), the OPENING-CONTEST row that appears
 * under the opponent and above you while the dice are being thrown (34 each), and the column's
 * gaps. The contest rows are counted UNCONDITIONALLY — the board must not resize when they come
 * and go, and a board fitted to the taller case simply keeps that size for the whole match.
 */
const BOARD_CHROME_H = 38 + 38 + 30 + DIE_ZONE_MIN * 2 + 5 * 10;

/**
 * ARE THE OPENING DICE STILL WANTED ON THE FELT? — the render-time half of the rule, pulled out
 * so it can be pinned in both directions. `showDice` is this OR the live contest OR the
 * announcement's own state.
 *
 * ⚠️ `announceOver`, NOT `announced`. The visibility must hand over on «the announcement has
 * FINISHED», never on «it has STARTED»: the start is a ref written synchronously inside an
 * effect while the state it hands to lands a render later, so a render in between would hide
 * the dice for one frame — and a `Die3D` that re-mounts after that frame latches `throwing`
 * false and never tumbles. That is the online-only «the dice don't animate» defect, measured on
 * the deployed build (2026-09-04). See the call site.
 */
export function doozDiceVisible({
  contestLive,
  decided,
  sawContest,
  announceOver,
}: {
  /** The engine still has a live `openingRoll` contest. */
  readonly contestLive: boolean;
  /** `view.opening` is set — a contest has been decided at some point in this match. */
  readonly decided: boolean;
  /** …and THIS screen watched it happen, rather than arriving at a resumed board. */
  readonly sawContest: boolean;
  /** The 3.6 s announcement has run its course. */
  readonly announceOver: boolean;
}): boolean {
  return !contestLive && decided && sawContest && !announceOver;
}

export function DoozTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  opponent,
  timer,
  chat = false,
}: {
  view: DoozView;
  legal: readonly DoozMove[];
  onMove: (move: DoozMove) => void;
  onLeave: () => void;
  self: DoozIdentity | null;
  opponent: DoozIdentity | null;
  timer?: TableTurnTimer | null;
  /** An ONLINE table: `TableChat`'s fixed toggle sits in this header's row, so its 40 px are kept
   *  free (see CHAT_TOGGLE_PX). `DoozOnlineGame` passes it on every online table, from the first
   *  frame; solo never does. */
  chat?: boolean;
}): ReactNode {
  const seatZeroColor = useContext(DoozColorContext);
  const you = view.you;
  const them = you === 0 ? 1 : 0;
  // ONE rule, from the seat: both screens draw the same disc in the same cell.
  const myColor = doozSeatColor(you, seatZeroColor);
  const theirColor = doozSeatColor(them, seatZeroColor);
  /*
   * ⚠️ `view.turn` IS A PLACEHOLDER WHILE THE CONTEST IS LIVE — the engine writes 0 until the dice
   * decide, exactly as تخته نرد's does. Without the third clause seat 0 (which is EVERY solo
   * human) gets a pulsing gold ring on their chip and «نوبت توئه — یک ستون رو نگه دار» under a
   * board nobody may play on yet. Only the server's empty `legalCols` was stopping the drop
   * itself, which is one guard too few for something the player can see.
   */
  const contest = view.openingRoll;
  const contestLive = contest !== undefined;
  const contestTies = contest?.ties ?? 0;
  /**
   * THAT SEAT'S DIE — the live throw, the pair a TIE is holding, or (once the contest is over) the
   * pair that DECIDED it.
   *
   * ⚠️ ALL THREE, IN THAT ORDER, AND THE LAST ONE IS WHY THE RESULT IS READABLE. The contest
   * resolves inside the SAME `applyMove` that rolls the second die, so the instant the second
   * player throws, `openingRoll` is gone — «when I roll, the result vanishes before the opponent
   * can see what happened». `view.opening` still carries the deciding pair, so the two dice stay
   * on the felt for the whole announcement. `tied` does the same job for a tie, which is the only
   * carrier of the second throw there.
   */
  const dieOf = (seat: number): number | null => {
    const index = seat === 0 ? 0 : 1;
    return (
      contest?.dice[index] ?? contest?.tied?.[index] ?? (view.opening ? view.opening[index] : null)
    );
  };
  const canThrow = legal.some((m) => m.kind === "openingRoll");

  /*
   * THE OPPONENT'S NAME — a real person's, else «ربات».
   *
   * ⚠️ «حریف» WAS WRONG AND EVERY OTHER TABLE ALREADY KNEW IT. A seat with nobody behind it is a
   * BOT, and the platform says «ربات» everywhere else; دوز was the one screen that hid what it was
   * playing against. `displayName` falls back to the generic «کاربر» when it has nothing real to
   * work with, which reads worse than the honest label — so that counts as no name at all, exactly
   * as `resolveSeatName` treats it.
   */
  const oppName = ((): string => {
    const name = opponent ? displayName(opponent) : "";
    return name && name !== fa.friends.user ? name : fa.online.bot;
  })();
  const tiedNow = contestTies > 0 && contest?.dice[0] === null && contest?.dice[1] === null;

  /* ---- who opens, announced once ---------------------------------------------------------- *
   * ⚠️ ADOPT AT MOUNT, AND DEPEND ON NOTHING BUT BOOLEANS.
   *
   * `view.opening` stays on the state for the whole match, so «تاس با تو افتاد» is equally true of
   * a board RESUMED at hand 2 and of every reconnect — announcing there is the same mistake as
   * replaying a burst of drop sounds on reconnect. `sawContest` is what makes this «a contest THIS
   * screen watched» rather than «a contest happened once».
   *
   * ⚠️ AND THE DEPENDENCY LIST IS BOOLEANS ONLY. `view.opening` is a fresh ARRAY on every socket
   * frame; listing it would re-run this effect on every unrelated update, and because React runs
   * the CLEANUP first the pending hide-timeout would be cleared while the `announced` guard
   * blocked re-arming it — the banner would stay for the rest of the match. That was the reported
   * bug, and it is the same gotcha as the frozen board, in this same file.
   * ------------------------------------------------------------------------------------------ */
  const decided = view.opening !== undefined;
  const [announcing, setAnnouncing] = useState(false);
  const sawContest = useRef(false);
  if (contestLive) sawContest.current = true;
  /** The announcement has been STARTED (so it is never armed twice). */
  const announced = useRef(false);
  /** …and it has FINISHED. This, not `announced`, is what hides the dice — see below. */
  const announceOver = useRef(false);
  /*
   * ⚠️ NO FRAME WITHOUT THE DICE. `announcing` is set in an EFFECT, so the render in which the
   * contest resolves would have `contestLive` false and `announcing` not yet true — both cubes
   * would UNMOUNT for one frame and mount again, throwing a second time. `pendingAnnounce` is the
   * same condition computed during render, so the dice never blink out.
   *
   * ⚠️⚠️ AND IT MUST HAND OVER ON «THE ANNOUNCEMENT IS OVER», NEVER ON «IT HAS STARTED» — that
   * one word was the whole of «the dice don't animate on an ONLINE table» (reported 2026-09-04,
   * solo unaffected). `announced.current = true` is written SYNCHRONOUSLY in the effect while
   * `setAnnouncing(true)` lands in a LATER render, so any render in between has neither: the
   * dice unmount and mount again with `throwing` false, and `Die3D` latches `throwing` AT MOUNT
   * — so the cube that then sits on the felt for the whole announcement never tumbles. Online
   * there IS such a render (the server arms the first turn window in the same instant the
   * contest resolves, and the store is external — a socket frame forces a synchronous re-render
   * that does not carry the queued state); in solo nothing arrives in that gap, which is exactly
   * why solo looked fine. MEASURED on the deployed build with a MutationObserver over both cubes,
   * `apps/web/tools/dooz-probes/probe-online-dice.cjs`: solo mounts each cube ONCE with
   * `throwing=1`; public and private both showed «mount throwing=1 → unmount 1 ms later → mount
   * throwing=0», on BOTH clients.
   *
   * `announceOver` is written in the SAME callback as `setAnnouncing(false)`, and it only ever
   * makes the dice LESS visible — so a render caught between those two sees `announcing` still
   * true and there is no gap in this direction either.
   */
  const pendingAnnounce = doozDiceVisible({
    contestLive,
    decided,
    sawContest: sawContest.current,
    announceOver: announceOver.current,
  });
  /** The dice stay on the felt while the contest is live AND for the whole announcement, so both
   *  players get to read the numbers before the board takes over. */
  const showDice = contestLive || announcing || pendingAnnounce;

  /*
   * WHICH DIE IS LANDING RIGHT NOW — TRACKED PER SEAT.
   *
   * ⚠️ «IS THE CONTEST STILL LIVE?» IS THE WRONG TEST, and this is تخته نرد's lesson verbatim
   * (`BackgammonTable.tsx`, «WHICH CONTEST DIE IS LANDING RIGHT NOW»). The SECOND die to land is
   * the one that RESOLVES the contest, so by the time that frame arrives `openingRoll` is already
   * gone. A die throws exactly once: when ITS OWN value first appears.
   *
   * Adopt at mount — a die already on the felt when we arrive was thrown before we got here, so a
   * resume replays nothing. Both slots reset when the felt is empty again, which is a fresh
   * contest AND a tie: both really are new throws.
   */
  const dieValues: readonly [number | null, number | null] = [dieOf(0), dieOf(1)];
  const thrownRef = useRef<[boolean, boolean]>([false, false]);
  const adopted = useRef(false);
  if (!adopted.current) {
    adopted.current = true;
    thrownRef.current = [dieValues[0] !== null, dieValues[1] !== null];
  }
  // Idempotent, so safe during render (StrictMode renders twice). MARKING one thrown is not,
  // which is why that happens in an effect below.
  if (dieValues[0] === null && dieValues[1] === null) thrownRef.current = [false, false];
  /*
   * WHICH ROUND OF THE CONTEST THESE DICE BELONG TO — and the latch resets at each boundary, so
   * the dice thrown after a tie animate like any other throw.
   *
   * ⚠️⚠️ THE HELD `tied` PAIR IS NOT A NEW ROUND — IT IS THE ROUND THAT JUST ENDED. Keying it as
   * one («a tie starts a new round») makes the tie frame a BOUNDARY: the latch resets and the
   * OPPONENT's die — which has been sitting on the felt for seconds — throws again in the very
   * instant the deciding one lands. تخته نرد shipped exactly that, had it reported from prod, and
   * wrote the rule down: the engine clears `dice` and holds the pair on `tied` in the SAME call
   * that increments `ties`, so the pair on screen belongs to round `ties − 1`. Say that, and every
   * case falls out — the tie frame keeps the round (only the die that just appeared animates), the
   * re-throws that follow are round `ties` and DO animate, and the resolution keeps the held round
   * so only the deciding die animates.
   *
   * ⚠️ AND IT IS HELD IN A REF, because `openingRoll` is gone the moment the contest resolves —
   * without that the round would collapse back to «0» on the deciding frame and reset the latch.
   */
  const liveRound = contest ? String(contestTies - (contest.tied ? 1 : 0)) : null;
  const heldRound = useRef(liveRound ?? "0");
  // Idempotent (same input ⇒ same value), so safe during render.
  if (liveRound !== null) heldRound.current = liveRound;
  const roundRef = useRef(heldRound.current);
  if (roundRef.current !== heldRound.current) {
    roundRef.current = heldRound.current;
    thrownRef.current = [false, false];
  }
  const throwingNow: readonly [boolean, boolean] = [
    dieValues[0] !== null && !thrownRef.current[0],
    dieValues[1] !== null && !thrownRef.current[1],
  ];
  /* ⚠️ MARKED IN AN EFFECT, NEVER DURING RENDER — an inline flip makes StrictMode's second pass
   * compute `throwing: false` and commit a die that never throws at all. */
  useEffect(() => {
    if (throwingNow[0]) thrownRef.current[0] = true;
    if (throwingNow[1]) thrownRef.current[1] = true;
  });
  useEffect(() => {
    if (contestLive || !decided || announced.current || !sawContest.current) return;
    announced.current = true;
    setAnnouncing(true);
    const id = window.setTimeout(() => {
      // BOTH, in this order and in one callback — see `pendingAnnounce`.
      announceOver.current = true;
      setAnnouncing(false);
    }, OPENING_ANNOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [contestLive, decided]);
  const yourTurn =
    view.turn === you && view.status === "playing" && !view.awaitingNextHand && !contestLive;

  useDoozSfx(view);

  /* ---- the FALL ------------------------------------------------------------------------- *
   * Keyed on the MONOTONIC `moveCount`, never on `lastDrop`'s identity: every frame arrives as
   * fresh JSON over the socket, so the drop is a NEW object with identical contents on every
   * unrelated update and comparing identity re-fires the animation (and its sound) on a frame
   * that has nothing to do with it. That is منچ's lesson, paid for once already.
   * ---------------------------------------------------------------------------------------- */
  const [falling, setFalling] = useState<{ cell: number; seat: number; ms: number } | null>(null);
  /** The CURRENT view, so the effect below can read it without depending on its identity. */
  const latest = useRef(view);
  latest.current = view;
  /*
   * ⚠️ THE DEPENDENCY IS `moveCount` ALONE — A NUMBER — AND THAT IS LOAD-BEARING, NOT TIDINESS.
   *
   * `view.lastDrop` is a NEW object on every frame (each one arrives as fresh JSON over the
   * socket), so listing it would re-run this effect on every unrelated update. React runs the
   * CLEANUP first, so the pending «the piece has landed» timeout would be cleared, a `moveCount`
   * guard would then return early, and nothing would re-arm it: `falling` would stay set for ever.
   * `busy` is derived from it and gates `canDrop`, so the player could never drop again — a frozen
   * board for the rest of the hand, from one extra frame arriving inside a 400 ms window.
   */
  useEffect(() => {
    const drop = latest.current.lastDrop;
    if (!drop) {
      // A `nextHand` — a fresh board, and nothing to animate.
      setFalling(null);
      return;
    }
    const ms = dropFallMs(drop.row);
    setFalling({ cell: drop.cell, seat: drop.seat, ms });
    const id = window.setTimeout(() => setFalling(null), ms + 30);
    return () => window.clearTimeout(id);
  }, [view.moveCount]);

  /* ---- press → preview → release --------------------------------------------------------- */
  const [previewCol, setPreviewCol] = useState<number | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  /** A press is LIVE. The ref is the idempotency guard (the board's own `pointerup` and the
   *  window backstop both fire for one release); the state is what arms the backstop. */
  const active = useRef(false);
  const [pressing, setPressing] = useState(false);
  // A drop is refused while a piece is still in the air, so a fast double-tap cannot play twice.
  const busy = falling !== null;
  const canDrop = yourTurn && !busy;

  /**
   * WHICH COLUMN A CLIENT-X IS OVER — read off the cells the browser actually PAINTED, never
   * computed from the board's width.
   *
   * ⚠️ THIS IS THE MIRRORED-COLUMN BUG AND ITS FIX, AND THE FIX IS THE METHOD, NOT THE ANSWER.
   * This used to be `Math.floor((clientX - rect.left) / rect.width * COLS)` under a comment
   * asserting «the board is drawn left-to-right whatever the page direction». It is not: the app
   * is `<html dir="rtl">` and this board is a CSS grid, so its column TRACKS lay out
   * right-to-left and engine column 0 paints at the RIGHT edge. The arithmetic named that edge
   * column 6. Reported from staging as «I aim for a cell on the right and the piece goes to a
   * cell on the left».
   *
   * ⚠️ AND IT SURVIVED 15/15 + 7/7 OF PROBE COVERAGE, BECAUSE THE PREVIEW AND THE DROP BOTH CAME
   * FROM THIS ONE NUMBER — they agreed with each other perfectly while disagreeing with the
   * player, and the probes pressed at a coordinate they had built with this same formula. Two
   * copies of one mistake cancel. Asking the DOM where a column really is cannot: the column the
   * finger is over is, by construction, the column whose painted box contains the finger.
   *
   * Outside the grid (the frame, or a finger dragged past the edge) the NEAREST column wins, so a
   * drag off the side still previews the end column rather than dropping the press.
   */
  const columnAt = (clientX: number): number | null => {
    const el = boardRef.current;
    if (!el) return null;
    // Row 0 is the floor — one cell per column, and every column has one.
    const cells = el.querySelectorAll<HTMLElement>('[data-row="0"]');
    let best: number | null = null;
    let bestDist = Infinity;
    for (const cell of cells) {
      const col = Number(cell.dataset["col"]);
      if (!Number.isInteger(col)) continue;
      const box = cell.getBoundingClientRect();
      if (box.width <= 0) continue;
      if (clientX >= box.left && clientX < box.right) return col;
      const dist = clientX < box.left ? box.left - clientX : clientX - box.right;
      if (dist < bestDist) {
        bestDist = dist;
        best = col;
      }
    }
    return best;
  };

  const playable = (col: number | null): col is number =>
    col !== null && (view.legalCols as readonly number[]).includes(col);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!canDrop) return;
    active.current = true;
    setPressing(true);
    // Capture keeps the move/up events coming even if the finger leaves the board. It THROWS for a
    // synthetic event carrying a pointerId the browser never issued (which is what a probe
    // dispatches), and the window backstop below covers exactly that case — so a failure here is
    // not fatal and must not stop the press.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* not a live pointer (a synthesised event) — the window listener covers it */
    }
    setPreviewCol(columnAt(e.clientX));
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!active.current || !canDrop) return;
    setPreviewCol(columnAt(e.clientX));
  };

  const release = (clientX: number | null): void => {
    // Idempotent: the board's own `pointerup` and the window backstop both fire for ONE release.
    if (!active.current) return;
    active.current = false;
    setPressing(false);
    const col = clientX === null ? previewCol : columnAt(clientX);
    setPreviewCol(null);
    if (!canDrop || !playable(col)) return;
    onMove({ kind: "drop", col });
  };

  const cancel = (): void => {
    active.current = false;
    setPressing(false);
    setPreviewCol(null);
  };

  /*
   * THE BACKSTOP — a finger dragged off the board, or a synthetic sequence that could not take
   * pointer capture, still releases.
   *
   * The handlers are read through a ref rather than captured in the effect, so the listener is
   * attached ONCE per press and always calls the CURRENT closure: re-attaching it on every
   * `previewCol` change would tear down and re-add a listener on every pixel of a drag.
   */
  const handlers = useRef({ release, cancel });
  handlers.current = { release, cancel };
  useEffect(() => {
    if (!pressing) return;
    const up = (e: PointerEvent): void => handlers.current.release(e.clientX);
    const abort = (): void => handlers.current.cancel();
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", abort);
    return () => {
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", abort);
    };
  }, [pressing]);

  /* ---- sizing ---------------------------------------------------------------------------- */
  const { ref: boxRef, height, width } = useBoxHeight<HTMLDivElement>();
  const usableH = Math.max(0, height - BOARD_CHROME_H);
  const cell = Math.max(
    18,
    Math.min(
      MAX_CELL,
      Math.floor(Math.min(width / (COLS + 2 * FRAME_RATIO), usableH / (ROWS + 2 * FRAME_RATIO))),
    ),
  );
  const frame = Math.round(cell * FRAME_RATIO);
  const boardW = cell * COLS + frame * 2;
  const boardH = cell * ROWS + frame * 2;
  const disc = Math.round(cell * DISC_RATIO);
  /*
   * THE DIE ZONES TAKE THE ROOM THAT IS ACTUALLY LEFT.
   *
   * ⚠️ A FIXED 52px WAS «TINY AND CRAMMED». The board is fitted first, to `usableH`; whatever the
   * column has spare after that is empty screen — a lot of it on a tall phone (the board is
   * width-limited at 384×823). Splitting the remainder between the two zones puts the dice in the
   * space that already existed instead of leaving it blank.
   *
   * ⚠️ IT IS COMPUTED FROM THE MEASURED COLUMN, NOT FROM THE CONTEST, so it is the SAME number
   * whether or not a die is on screen — which is what stops the profiles jumping when the contest
   * ends. `BOARD_CHROME_H` reserves the FLOOR, so the board can never be squeezed by this.
   */
  const spare = Math.max(0, height - BOARD_CHROME_H - (cell * ROWS + frame * 2));
  const dieZone = Math.max(DIE_ZONE_MIN, Math.min(DIE_ZONE_MAX, DIE_ZONE_MIN + spare / 2));

  const winCells = new Set<number>(view.handResult?.line ?? []);
  const previewRow = previewCol !== null ? (view.landing[previewCol] ?? -1) : -1;
  const showPreview = playable(previewCol) && previewRow >= 0;

  /*
   * ONE LINE CARRIES EVERY MESSAGE, in this order: the contest asks for a throw (and says «مساوی
   * شد» when a tie sends both dice back), then the announcement of who opens, then the turn, then
   * a decided hand's result. It is fixed-height, so the board never moves when the words change.
   */
  const hint = contestLive
    ? tiedNow
      ? fa.dooz.rollTied
      : canThrow
        ? fa.dooz.rollWaitingYou
        : fa.dooz.rollWaitingThem
    : announcing
      ? view.opener === you
        ? fa.dooz.dieYouStart
        : fa.dooz.dieTheyStart
      : view.awaitingNextHand
        ? handResultLine(view)
        : view.status === "over"
          ? ""
          : yourTurn
            ? fa.dooz.turnYours
            : fa.dooz.turnTheirs;

  return (
    <div
      style={{
        position: "relative",
        height: "var(--app-h)",
        display: "flex",
        flexDirection: "column",
        background:
          "radial-gradient(130% 120% at 50% 30%, var(--felt-2) 0%, var(--felt-edge) 55%, var(--boot-vignette) 100%)",
        color: "var(--text)",
        overflow: "hidden",
      }}
    >
      <div
        /*
         * ONE 40 px BAND, THE WAY حکم's HEADER IS (operator, 2026-10-08: «the title and «دست x
         * از y» under it sit lower than the exit button and the score box — line them up the way
         * حکم's header is»). Exit · the chat toggle's slot (online) · «دوز» · the score box, each
         * EXACTLY the buttons' 40 px: «دست x از y» is the box's first column and nothing hangs
         * under the title, so the four share one centre — MEASURED (`tools/dooz-probes/
         * probe-header.cjs`) at all 20 sizes, solo and online. The two-line stack it replaces
         * («دوز» over the counter pill, 48 px beside 40 px buttons) hung 8 px below them: its
         * text 6.5 px above the exit's centre, the counter 18.25 px below it.
         *
         * ⚠️ `items-start` STAYS (2026-09-03, measured): the exit must start on the `fixed` chat
         * toggle's line, the padding edge. With every child 40 px it is the same as `items-center`
         * — and it keeps the exit there if a child ever grows again.
         *
         * `data-table-hud`: the end card's shell starts below this band and never covers it.
         */
        data-table-hud
        className="absolute inset-x-0 top-0 z-10 flex items-start gap-2 px-4"
        /*
         * ⚠️ 10px — THE SAME OFFSET `TableChat`'s TOGGLE USES, and every other table with it.
         *
         * This was 8px, and دوز was the only table in the app that used it. On an ONLINE table the
         * chat toggle is a `fixed` 40 px circle at `10px + safeTop` while the exit button sat at
         * `8px + safeTop`, so the two controls — and the title between them — were two pixels off
         * each other's axis. Reported as «the chat button and the دوز title aren't aligned with
         * the exit button». Pinned in `dooz-table.test.tsx` against `TableChat`'s own source, so
         * moving either one alone reddens.
         */
        style={{
          paddingTop: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <button
          type="button"
          onClick={onLeave}
          aria-label={fa.dooz.leave}
          data-testid="dooz-exit"
          className={`${BTN} flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
        >
          <GameIcon type="exit" size={20} />
        </button>
        {/* `TableChat`'s fixed toggle sits here — not in this flow, so its 40 px are kept free
            (see CHAT_TOGGLE_PX). On every online table, from the first frame; never in solo. */}
        {chat ? (
          <div
            aria-hidden
            data-testid="chat-toggle-slot"
            className="h-10 flex-none"
            style={{ width: CHAT_TOGGLE_PX }}
          />
        ) : null}

        {/*
         * THE TITLE, ALONE ON THE BAND — centred in the 40 px between the exit (solo) or the chat
         * toggle's slot (online) and the score box. The hand counter that hung under it is the
         * box's first column now (operator, 2026-10-08).
         *
         * ⚠️ BELOW 300 px AN ONLINE BAND CANNOT HOLD ALL FIVE. Exit + gap + the toggle's slot + gap
         * is 96 px, the box below 380 px is 128: at 256 px that is the whole 224 px row, with
         * nothing left for «دوز» (29 px) and its gap — MEASURED at 256×393. The fallback that
         * keeps the counter under the title (an 80–86 px pill in a 41 px column) overlaps there too,
         * and so does today's header (the toggle sat 10 px into «دوز» and 36 px into the pill).
         * So the TITLE gives way at that width, online only: it names the game the player chose,
         * the counter is the match's state. Solo at 256 px keeps it (the row has no slot).
         */}
        <div
          className={`flex h-10 min-w-0 flex-1 items-center justify-center ${
            chat ? "max-[299px]:hidden" : ""
          }`}
        >
          <span className="font-headline-md text-headline-md font-bold leading-tight text-secondary">
            {fa.dooz.title}
          </span>
        </div>

        {/*
         * THE SCORE BOX — «دست x از y» FIRST (rightmost under rtl), then «شما» and «حریف», each a
         * label over its number, on the title's band (operator, 2026-09-03; the hand column
         * 2026-10-08, the way حکم's box reads «دست n» first).
         *
         * ⚠️ IT NAMES ITS TWO SIDES. «شما» over your hands, «حریف» over theirs — a viewer-relative
         * «۲ – ۱» with no legend was the thing that made the old line unreadable, and no amount of
         * moving it would have fixed that.
         *
         * ⚠️ EXACTLY 40 px (`h-10`, no `min-height`): the label + number stack is 3 + 12 + 20 + 3,
         * centred in the buttons' height. Every column is a FIXED width (SCORE_COL), so the header
         * never shifts as the numbers change; below 380 px the columns' padding tightens so the
         * title keeps its room, as حکم's box does. `ms-auto` keeps it at the band's far end on the
         * narrow online band where the title gives way.
         *
         * ⚠️ THE HAND IS A LABEL OVER A NUMBER, NOT ONE LINE. One line in the box's caption size
         * was ruled unreadable on 2026-09-03 («`body-md`, NOT `caption`»), and one line in
         * `body-md` (73.7 px) does not fit beside «دوز» at 320 px online; «۱ از ۳» in the box's
         * own number style (16 px bold) under «دست» does, and reads like its two neighbours.
         */}
        <div
          data-testid="dooz-scorebox"
          data-you={view.wins[you] ?? 0}
          data-them={view.wins[them] ?? 0}
          className="ms-auto flex h-10 flex-none items-stretch overflow-hidden rounded-lg border border-outline-variant bg-surface-container/80 text-center shadow-sm"
        >
          <div
            data-testid="dooz-score"
            className="box-content flex flex-none flex-col justify-center px-2 py-[3px] max-[379px]:px-1.5"
            style={{ width: SCORE_COL.hand }}
          >
            <span className="font-caption text-caption leading-none text-on-surface-variant">
              {fa.dooz.handLabel}
            </span>
            <span className="whitespace-nowrap font-body-lg text-body-lg font-bold leading-tight text-on-surface">
              {fa.dooz.handOf
                .replace("{hand}", faNum(view.hand))
                .replace("{total}", faNum(view.handsToPlay))}
            </span>
          </div>
          <div aria-hidden className="h-6 w-px flex-none self-center bg-outline-variant" />
          {(
            [
              [fa.dooz.scoreYou, view.wins[you] ?? 0, true],
              [fa.dooz.scoreThem, view.wins[them] ?? 0, false],
            ] as const
          ).map(([label, n, mine], i) => (
            <div
              key={label}
              className={`box-content flex flex-none flex-col justify-center px-2 py-[3px] max-[379px]:px-1.5 ${
                i === 1 ? "border-s border-outline-variant" : ""
              }`}
              style={{ width: mine ? SCORE_COL.you : SCORE_COL.them }}
            >
              <span className="font-caption text-caption leading-none text-on-surface-variant">
                {label}
              </span>
              <span
                className={`font-body-lg text-body-lg font-bold leading-tight tabular-nums ${
                  mine ? "text-secondary" : "text-on-surface"
                }`}
              >
                {faNum(n)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        ref={boxRef}
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          alignItems: "center",
          justifyContent: "center",
          padding:
            "calc(50px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px))) 12px" +
            " calc(10px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
        }}
      >
        {/*
         * THE OPPONENT'S DIE ZONE — above their chip, in the room at the top of the screen.
         *
         * ⚠️ THE HEIGHT IS RESERVED WHETHER OR NOT A DIE IS IN IT. «After the roll the player
         * profiles jump upward» was this row appearing and disappearing: the board is fitted to
         * whatever height is left, so a row that comes and goes reflows the ENTIRE table. It is
         * always here, always `DIE_ZONE_H` tall, and `BOARD_CHROME_H` counts it — so the board is
         * sized once, for the tallest case, and nothing moves for the rest of the match.
         */}
        <div
          data-testid="dooz-die-zone-them"
          style={{ width: "100%", maxWidth: 420, height: dieZone }}
          className="flex items-center justify-center"
        >
          {showDice ? (
            <DieSlot
              value={dieValues[them] ?? null}
              seat={them}
              round={heldRound.current}
              mine={false}
              throwing={throwingNow[them] ?? false}
            />
          ) : null}
        </div>

        <div style={{ width: "100%", maxWidth: 420 }}>
          <SeatStrip
            /* ⚠️ «ربات», NOT «حریف». A seat with no real person behind it is a BOT, and every
               other table in this app says so — a solo game announcing «حریف» is the only screen
               that hid what it was playing against. `displayName` returns the generic «کاربر»
               when it has nothing real, which reads worse than the honest label, so that counts
               as no name at all (the `resolveSeatName` rule). */
            name={oppName}
            who={opponent}
            color={theirColor}
            mine={false}
            active={
              view.turn === them &&
              view.status === "playing" &&
              !view.awaitingNextHand &&
              !contestLive
            }
            slot={
              // ⚠️ `timer.turn` IS THE FRAME, NOT A SEAT — the seat is `timer.turn.seat`.
              timer && view.status === "playing" && timer.turn.seat === them ? (
                <span data-testid="dooz-seat-timer">
                  <TurnTimer turn={timer.turn} you={them} totalSec={timer.totalSec} compact />
                </span>
              ) : undefined
            }
          />
        </div>

        {/* THE BOARD — one pointer surface. `touch-action: none` so a vertical drag inside it is a
            column drag rather than a page scroll. */}
        <div
          ref={boardRef}
          data-testid="dooz-board"
          role="group"
          aria-label={fa.dooz.boardLabel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => release(e.clientX)}
          onPointerCancel={cancel}
          onLostPointerCapture={cancel}
          style={{
            touchAction: "none",
            /* ⚠️ THE GRID IS EXPLICITLY LTR, so the column TRACKS paint in the same order as the
               engine's own indices and `data-col` reads left-to-right on screen. Inherited from
               `<html dir="rtl">` this grid laid its tracks out right-to-left, which put column 0
               at the right edge — invisible in a diff, invisible to every unit test (happy-dom
               runs no layout) and the reason the mirror survived. `columnAt` no longer depends on
               it either way; this is what stops the index and the paint disagreeing again. */
            direction: "ltr",
            width: boardW,
            height: boardH,
            padding: frame,
            boxSizing: "border-box",
            display: "grid",
            gridTemplateColumns: `repeat(${COLS}, ${cell}px)`,
            gridTemplateRows: `repeat(${ROWS}, ${cell}px)`,
            borderRadius: Math.round(cell * 0.24),
            /*
             * DARK WALNUT WITH A THIN BRASS BEAD, standing on the felt — the board the artwork
             * actually shows (`apps/web/tools/dooz-art/PROMPTS.md`), not the flat blue plastic
             * this was. The wood is تخته نرد's OWN rim tokens (`--c-bg-rim` / `--c-bg-rim-2`) and
             * the bead is `--c-gold-deep` (#c79a3f), so دوز's board is the same walnut-and-brass
             * set as شطرنج's and تخته نرد's rather than a fourth material invented here.
             */
            /*
             * ⚠️ THE LIT TONE IS `--wood`, NOT `--c-bg-rim`. Built from the rim pair alone
             * (#2b1810 → #1a0f0a) the frame photographed as a near-black slab: the holes are
             * #0a2a1f and there was barely a step between them, so the board read flat and the
             * recesses stopped reading as recesses. `--wood` (#3a2a19) is the app's own walnut and
             * gives the step back while staying in the same family as شطرنج's set and تخته نرد's
             * rim. Measured on the deployed build at all four viewports.
             */
            background:
              "linear-gradient(168deg, var(--wood) 0%, rgb(var(--c-bg-rim)) 55%," +
              " rgb(var(--c-bg-rim-2)) 100%)",
            // The bead is a BORDER plus an inset ring: one hairline of brass catching the light on
            // the outer edge, and a darker seat just inside it so the frame reads as thick.
            border: "1px solid var(--c-gold-deep, #c79a3f)",
            boxShadow:
              "0 18px 40px rgba(0,0,0,.5), inset 0 0 0 1px rgba(199,154,63,.55)," +
              " inset 0 2px 3px rgba(0,0,0,.55), inset 0 -2px 3px rgba(255,255,255,.06)",
            cursor: canDrop ? "pointer" : "default",
            position: "relative",
          }}
        >
          {/* Rows are drawn TOP-DOWN: engine row ROWS-1 first. The engine's row 0 is the floor. */}
          {Array.from({ length: ROWS }, (_, i) => ROWS - 1 - i).map((row) =>
            Array.from({ length: COLS }, (_, col) => {
              const index = row * COLS + col;
              const owner = view.cells[index] ?? EMPTY;
              const isFalling = falling?.cell === index;
              const ghost = showPreview && previewCol === col && previewRow === row;
              const won = winCells.has(index);
              return (
                <div
                  key={index}
                  data-testid={`dooz-cell-${row}-${col}`}
                  data-col={col}
                  data-row={row}
                  data-owner={String(owner)}
                  data-win={won ? "1" : undefined}
                  style={{
                    width: cell,
                    height: cell,
                    display: "grid",
                    placeItems: "center",
                    position: "relative",
                  }}
                >
                  {/*
                   * THE HOLE — «deep and unlit, almost black» (the artwork's own words), with the
                   * felt's darkest tone at the bottom of it so an empty cell reads as a hole
                   * bored through the wood rather than a painted circle. The rim light along its
                   * top edge is what gives it depth against a LIGHT ground; the old flat
                   * `rgba(6,16,26,.72)` was tuned for a blue board and reads as a smudge on wood.
                   */}
                  <span
                    aria-hidden
                    data-testid="dooz-hole"
                    style={{
                      position: "absolute",
                      width: disc,
                      height: disc,
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle at 50% 62%, #06170f 0%, #0a2a1f 78%, #0d3327 100%)",
                      // A hard top shadow and a brass glint on the lower rim: that pair is what
                      // makes a dark circle on dark wood read as a HOLE rather than a painted dot.
                      boxShadow:
                        "inset 0 3px 6px rgba(0,0,0,.9), inset 0 -1px 1px rgba(199,154,63,.28)," +
                        " 0 1px 0 rgba(255,255,255,.07)",
                      animation: won ? "dooz-win-pulse 1.1s ease-in-out infinite" : undefined,
                    }}
                  />
                  {owner !== EMPTY && !isFalling ? (
                    <span style={{ position: "relative" }}>
                      <Disc color={owner === you ? myColor : theirColor} size={disc} />
                    </span>
                  ) : null}
                  {isFalling && falling ? (
                    <span
                      data-testid="dooz-falling"
                      style={{
                        position: "relative",
                        // The disc starts one full board above its landing cell and eases down.
                        // The DURATION is the engine's own, so a bot can never move before the
                        // piece has landed (`postMoveDelayMs` returns this plus the settle).
                        /*
                         * ⚠️ THE TRAVEL IS MEASURED FROM THE **TOP** OF THE BOARD, and it has to
                         * be the same way round as the duration.
                         *
                         * This was `-(row + 1) * cell - frame` — the distance from the BOTTOM —
                         * while `dropFallMs` is `BASE + (ROWS - 1 - row) * PER_ROW`, measured from
                         * the top. The two were exact inverses: a piece landing on the FLOOR (the
                         * longest fall there is) crawled ONE CELL over the LONGEST duration, and
                         * one landing on the top row flew the whole board in the shortest. That is
                         * what «pieces look like they lift off their cells and drop again» was.
                         * `dooz-table.test.tsx` now pins distance and duration as the same
                         * monotonic function of the row, red under the old expression.
                         */
                        ["--dooz-fall" as string]: `${-((ROWS - row) * cell + frame)}px`,
                        ["--dooz-bounce" as string]: `${-Math.round(cell * 0.09)}px`,
                        animation: `dooz-drop ${falling.ms}ms both`,
                      }}
                    >
                      <Disc color={falling.seat === you ? myColor : theirColor} size={disc} />
                    </span>
                  ) : null}
                  {ghost ? (
                    <span
                      data-testid="dooz-preview"
                      data-col={col}
                      data-row={row}
                      style={{ position: "relative", opacity: 0.42 }}
                    >
                      <Disc color={myColor} size={disc} />
                    </span>
                  ) : null}
                </div>
              );
            }),
          )}
        </div>

        {/*
         * THE HINT LINE. Fixed height so the board never moves when the text changes.
         *
         * ⚠️ THERE IS NO «دست بعد» BUTTON (operator, 2026-09-03). The pause already advanced on
         * its own — a bot plays `nextHand` after the reveal in solo, and the room's shared window
         * does it on a human table — so the button was a second way to do a thing that was
         * happening anyway, and offering it made the automatic advance look like a bug. The reveal
         * is `HAND_REVEAL_MS` and the room's window for this phase is now the same number.
         *
         * ⚠️ AND THE TURN CLOCK IS NOT HERE EITHER: it is on each player's own chip, above and
         * below, where it says WHOSE turn is running out.
         */}
        <div
          style={{ width: "100%", maxWidth: 420, height: 30 }}
          className="flex items-center justify-center gap-3"
        >
          {/* ⚠️ `text-on-surface`, NOT the variant grey — this line is a PROMPT («تاس بریز تا
              معلوم بشه کی شروع می‌کنه»), and a prompt that reads as disabled text is one nobody
              acts on. It carries the turn, the hand's result and the contest's instruction, so it
              is the loudest text under the board. */}
          <span
            data-testid="dooz-hint"
            className="truncate px-2 font-body-md text-body-md font-bold text-on-surface"
          >
            {hint}
          </span>
        </div>

        <div style={{ width: "100%", maxWidth: 420 }}>
          <SeatStrip
            name={self ? displayName(self) : fa.dooz.you}
            who={self}
            color={myColor}
            mine
            active={yourTurn}
            /* THE «تاس بریز» BUTTON LIVES HERE — in the profile row, at its END, which under this
               app's RTL is its LEFT. Its own box, beside the name, where it cannot collide with
               anything and cannot float over the board. During play the same slot holds the clock;
               they are never both wanted, and one slot makes that structural. */
            slot={
              contestLive ? (
                <button
                  type="button"
                  data-testid="dooz-roll-cta"
                  disabled={!canThrow}
                  onClick={() => onMove({ kind: "openingRoll" })}
                  className={`${BTN} rounded-xl px-3 py-[6px] font-body-md text-body-md font-bold shadow-sm transition-transform active:scale-95 ${
                    canThrow
                      ? "bg-secondary text-ink-on-gold"
                      : "cursor-default bg-surface-container text-on-surface-variant opacity-60"
                  }`}
                >
                  🎲 {fa.dooz.rollCta}
                </button>
              ) : timer && view.status === "playing" && timer.turn.seat === you ? (
                <span data-testid="dooz-seat-timer">
                  <TurnTimer turn={timer.turn} you={you} totalSec={timer.totalSec} compact />
                </span>
              ) : undefined
            }
          />
        </div>

        {/*
         * YOUR DIE ZONE — below your chip, in the room between it and the bottom of the screen.
         * Reserved unconditionally, for the same reason the opponent's is: a row that comes and
         * goes re-fits the board and everything jumps.
         */}
        <div
          data-testid="dooz-die-zone-you"
          style={{ width: "100%", maxWidth: 420, height: dieZone }}
          className="flex items-center justify-center"
        >
          {showDice ? (
            <DieSlot
              value={dieValues[you] ?? null}
              seat={you}
              round={heldRound.current}
              mine
              throwing={throwingNow[you] ?? false}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** What the between-hands line says. */
function handResultLine(view: DoozView): string {
  const result = view.handResult;
  if (!result) return "";
  const hand = faNum(view.hand);
  if (result.winner === null) return fa.dooz.handDrawn.replace("{hand}", hand);
  return (result.winner === view.you ? fa.dooz.handWonByYou : fa.dooz.handWonByThem).replace(
    "{hand}",
    hand,
  );
}

/** How long the opening-die banner stays up. Short: it announces a fact, it does not ask for one. */
/**
 * ONE SEAT'S OPENING DIE — drawn on THAT PLAYER'S OWN SIDE of the board.
 *
 * ⚠️ IT IS NOT AN OVERLAY ANY MORE, AND THAT IS THE POINT. The first version put both dice in a
 * panel centred over the board. Two things were wrong with it: it sat on the columns (and, with
 * `pointer-events: auto`, MEASURABLY swallowed the first tap of the game — `probe-mirror` read
 * «nothing landed» at all four viewports), and it ignored the room this layout actually has, which
 * is above the opponent and below you. Each die now appears beside the player who threw it, where
 * «whose die is that?» needs no label at all.
 *
 * The cube is the SHARED 3-D die (`game/backgammon-dice`) — the very component منچ and تخته نرد
 * throw. A third implementation of a die is how two of them start disagreeing about what a 6
 * looks like.
 */
function DieSlot({
  value,
  seat,
  round,
  mine,
  throwing,
}: {
  value: number | null;
  seat: number;
  /** Which ROUND of the contest this die belongs to — see the caller. It is the cube's `key`. */
  round: string;
  mine: boolean;
  /** Is THIS die landing right now? Decided by the caller, per seat — see the latch there. */
  throwing: boolean;
}): ReactNode {
  return (
    <div
      data-testid={`dooz-die-${mine ? "you" : "them"}`}
      data-value={value ?? ""}
      /* Surfaced HERE rather than by patching the shared `Die3D` (منچ and تخته نرد render it too):
         it is the one fact a test needs about a throw — is THIS die landing right now? */
      data-throwing={value === null ? "" : throwing ? "1" : "0"}
      className="flex flex-none items-center justify-center"
      /* ⚠️ `top`, AND THE TWO SIGNS ARE OPPOSITE AND EQUAL — see {@link DIE_NUDGE}. Each die moves
         away from the middle of the screen: the opponent's DOWN, off the hand counter it was
         overlapping; yours UP by the same amount, so the pair stays symmetric about the board. */
      style={{
        width: DIE_SIZE + 14,
        height: DIE_SIZE + 14,
        position: "relative",
        top: mine ? -DIE_NUDGE : DIE_NUDGE,
      }}
    >
      {/*
       * ⚠️ NOTHING IS DRAWN UNTIL THERE IS A DIE. There used to be a dashed placeholder marking
       * the slot: it showed you where the die would land before it was thrown, which makes the
       * throw read as filling in a reserved square rather than as a throw. The die ARRIVES. The
       * enclosing zone still reserves the HEIGHT, so nothing moves when it does.
       *
       * ⚠️ THE `key` IS THE ROUND — NOT THE VALUE, AND NOT NOTHING. Both extremes were shipped
       * and both were wrong:
       *
       *   · keyed on the VALUE, every remount replayed the throw, and at the resolution both cubes
       *     briefly unmounted — «it shows the bot's roll twice»;
       *   · with NO key, a re-throw after a tie is an ATTRIBUTE CHANGE on a cube that never
       *     unmounted (the opponent's goes 5 → 6 in place), and `Die3D` latches `throwing` at
       *     mount — so it simply appears. MEASURED on the deployed build, `probe-tie.cjs`:
       *     «10698ms attr {id: dooz-die-them, value: "6", was: "5"}» with no mount beside it.
       *
       * The ROUND is the thing that means «this is a new throw», so it is what the identity keys
       * on. `throwing` still gates the animation for the adopt-at-mount case (a resume replays
       * nothing) and for the die that did not move.
       */}
      {value === null ? null : (
        <Die3D
          key={`${seat}-${round}`}
          value={value}
          size={DIE_SIZE}
          seed={`dooz-${seat}-${round}-${value}`}
          throwing={throwing}
          fromBottom={mine}
          alwaysDrawn
          testId={`dooz-die-cube-${mine ? "you" : "them"}`}
        />
      )}
    </div>
  );
}
